import { Box, ColorPicker, Divider, Flex, Text, Tooltip } from '@mantine/core';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './ColorPickerSolid.module.scss';
import { ColorInputSize, ColorSwatchSize } from '../../../types/ColorPicker';
import { isGradientColor } from '../../../utils/isGradientColor';
import { useConvertCssValueToHexaColor } from '../../../utils/useConvertCssValueToHexaColor';
import { ColorInput } from '../../inputs/ColorInput';
import { ColorEyeDropper } from '../ColorEyeDropper';
import { ColorSwatch } from '../ColorSwatch';

const DEFAULT_COLOR = '#FFFFFF00';

type ColorPickerSolidProps = {
  onSelectColor: (color: string) => void;
  color: string;
  themeColors: string[];
  withGradient?: boolean;
  editComponent: ReactNode;
};

export function ColorPickerSolid(props: ColorPickerSolidProps) {
  const { color, onSelectColor, themeColors, withGradient, editComponent } = props;
  const { t } = useTranslation('components.colorPicker');
  const resolveColor = useConvertCssValueToHexaColor();

  const solidColor = isGradientColor(color) ? DEFAULT_COLOR : color;
  return (
    <Flex className={classes.wrapper}>
      <ColorPicker
        value={solidColor || DEFAULT_COLOR}
        onChange={onSelectColor}
        format="hexa"
        fullWidth
        size="sm"
        classNames={{
          preview: classes.preview,
          sliders: classes.sliders,
          body: classes.body,
          slider: classes.slider,
          sliderOverlay: classes.sliderOverlay,
          saturation: classes.saturation,
          saturationOverlay: classes.saturationOverlay,
          thumb: classes.thumb
        }}
      />
      <Flex className={classes.inputWrapper}>
        <ColorInput color={solidColor} onSelectColor={onSelectColor} withOpacity size={ColorInputSize.small} />
        <ColorEyeDropper setColor={onSelectColor} />
      </Flex>

      {themeColors.length > 0 && (
        <>
          <Divider className={classes.divider} />
          <Flex className={classes.themeColorsWrapper}>
            <Flex className={classes.themeColorsTop}>
              <Text className={classes.themeColorsTitle}>{t('themeColors')}</Text>
              {editComponent}
            </Flex>

            <Flex className={classes.colorSwatchesWrapper}>
              {themeColors.map((themeColor, index) => {
                return isGradientColor(resolveColor(themeColor)) && !withGradient ? (
                  <Tooltip label={t('gradientsNotSupported')}>
                    <Box className={classes.notAllowed}>
                      <ColorSwatch
                        color={themeColor}
                        selected={themeColor.toLowerCase() === solidColor.toLowerCase()}
                        size={ColorSwatchSize.medium}
                      />
                    </Box>
                  </Tooltip>
                ) : (
                  <Box key={index} onClick={() => onSelectColor(themeColor)} className={classes.colorSwatch}>
                    <ColorSwatch
                      color={themeColor}
                      selected={themeColor.toLowerCase() === solidColor.toLowerCase()}
                      size={ColorSwatchSize.medium}
                    />
                  </Box>
                );
              })}
            </Flex>
          </Flex>
        </>
      )}
    </Flex>
  );
}
